// Aba "Briefing" do contrato. Mostra os campos de "briefing pra equipe"
// vindos do Notion Projetos: objetivos, requisitos, restrições, dependências,
// equipe, equipamentos, etc.
// Wave 2 (Antd) — 2026-06-01.

import { Row, Col, Card, Tag, Descriptions, Typography, Space } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import type { Contrato } from './types';

const titleStyle = {
  fontSize: 12, color: '#020D7F', fontWeight: 600,
  textTransform: 'uppercase' as const, letterSpacing: 0.5,
};

const cardTitle = (t: string) => <Typography.Text strong style={titleStyle}>{t}</Typography.Text>;

export function AbaBriefing({ contrato }: { contrato: Contrato }) {
  const tem = (s?: string) => s && s.trim().length > 0;

  const equipeItems = [
    { key: 'resp', label: 'Responsável', children: contrato.responsavel_email || '—' },
    { key: 'gestor', label: 'Gestor', children: contrato.gestor_email || '—' },
    { key: 'epp', label: 'Equipe principal', children: (contrato.equipe_emails && contrato.equipe_emails.length > 0)
      ? <Space direction="vertical" size={2}>{contrato.equipe_emails.map(e => <Typography.Text key={e} style={{ fontSize: 11 }}>· {e}</Typography.Text>)}</Space>
      : '—' },
    { key: 'epc', label: 'Equipe complementar', children: (contrato.equipe_complementar && contrato.equipe_complementar.length > 0)
      ? <Space direction="vertical" size={2}>{contrato.equipe_complementar.map(e => <Typography.Text key={e} type="secondary" style={{ fontSize: 11 }}>· {e}</Typography.Text>)}</Space>
      : '—' },
  ];

  const TextoBloco = ({ texto, color }: { texto: string; color?: string }) => (
    <Typography.Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginBottom: 0, color }}>
      {texto}
    </Typography.Paragraph>
  );

  return (
    <Row gutter={[14, 14]}>
      <Col span={24}>
        <Card size="small" title={cardTitle('Equipe')}>
          <Descriptions size="small" column={{ xs: 1, sm: 2 }} colon={false} items={equipeItems}
            labelStyle={{ color: '#888', fontSize: 12, width: 140 }}
            contentStyle={{ fontSize: 12 }} />
        </Card>
      </Col>

      {tem(contrato.objetivos) && (
        <Col span={24}>
          <Card size="small" title={cardTitle('Objetivos')}>
            <TextoBloco texto={contrato.objetivos!} />
          </Card>
        </Col>
      )}
      {tem(contrato.requisitos_especificos) && (
        <Col xs={24} md={12}>
          <Card size="small" title={cardTitle('Requisitos específicos')}>
            <TextoBloco texto={contrato.requisitos_especificos!} />
          </Card>
        </Col>
      )}
      {tem(contrato.restricoes_tecnicas) && (
        <Col xs={24} md={12}>
          <Card size="small" title={cardTitle('Restrições técnicas/operacionais')}>
            <TextoBloco texto={contrato.restricoes_tecnicas!} />
          </Card>
        </Col>
      )}
      {tem(contrato.dependencias_terceiros) && (
        <Col xs={24} md={12}>
          <Card size="small" title={cardTitle('Dependências de terceiros')}>
            <TextoBloco texto={contrato.dependencias_terceiros!} />
          </Card>
        </Col>
      )}
      {tem(contrato.cronograma_texto) && (
        <Col xs={24} md={12}>
          <Card size="small" title={cardTitle('Cronograma')}>
            <TextoBloco texto={contrato.cronograma_texto!} />
          </Card>
        </Col>
      )}
      {tem(contrato.entregas_texto) && (
        <Col xs={24} md={12}>
          <Card size="small" title={cardTitle('Entregas')}>
            <TextoBloco texto={contrato.entregas_texto!} />
          </Card>
        </Col>
      )}
      {tem(contrato.follow_up) && (
        <Col span={24}>
          <Card size="small" title={cardTitle('Follow-up')}>
            <TextoBloco texto={contrato.follow_up!} color="#B07500" />
          </Card>
        </Col>
      )}

      {(tem(contrato.contato_nome) || tem(contrato.contato_ref_2_nome)) && (
        <Col span={24}>
          <Card size="small" title={cardTitle('Contatos do cliente')}>
            <Row gutter={14}>
              {tem(contrato.contato_nome) && (
                <Col xs={24} md={12}>
                  <Typography.Text strong style={{ fontSize: 11, color: '#020D7F' }}>Principal</Typography.Text>
                  <Descriptions size="small" column={1} colon={false}
                    labelStyle={{ color: '#888', fontSize: 11, width: 90 }}
                    contentStyle={{ fontSize: 12 }}
                    items={[
                      { key: 'n', label: 'Nome', children: contrato.contato_nome || '—' },
                      { key: 'e', label: 'E-mail', children: contrato.contato_email || '—' },
                      { key: 't', label: 'Telefone', children: contrato.contato_telefone || '—' },
                    ]} />
                </Col>
              )}
              {tem(contrato.contato_ref_2_nome) && (
                <Col xs={24} md={12}>
                  <Typography.Text strong style={{ fontSize: 11, color: '#020D7F' }}>Secundário</Typography.Text>
                  <Descriptions size="small" column={1} colon={false}
                    labelStyle={{ color: '#888', fontSize: 11, width: 90 }}
                    contentStyle={{ fontSize: 12 }}
                    items={[
                      { key: 'n', label: 'Nome', children: contrato.contato_ref_2_nome || '—' },
                      { key: 'e', label: 'E-mail', children: contrato.contato_ref_2_email || '—' },
                      { key: 'c', label: 'Celular', children: contrato.contato_ref_2_celular || '—' },
                    ]} />
                </Col>
              )}
            </Row>
          </Card>
        </Col>
      )}

      {(contrato.arquivos_notion_urls && contrato.arquivos_notion_urls.length > 0) && (
        <Col span={24}>
          <Card size="small" title={cardTitle('Arquivos (Notion)')}>
            <Space direction="vertical" size={4}>
              {contrato.arquivos_notion_urls.map((a, i) => (
                <Typography.Link key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12 }}>
                  <LinkOutlined /> {a.nome || `Arquivo ${i + 1}`}
                </Typography.Link>
              ))}
            </Space>
          </Card>
        </Col>
      )}
    </Row>
  );
}
